import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Select,
  Spin,
  Descriptions,
} from "antd";
import { PlusOutlined, SearchOutlined, CloseOutlined } from "@ant-design/icons";
import useApis from "../apis/useApis";
import appConstant from "./../json/appConstant.json";
import DepartmentFilter from "./DepartmentFilter";

const { Option } = Select;

const Departments = ({ isDarkMode }) => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [selectedDepartmentForStatus, setSelectedDepartmentForStatus] =
    useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [dateFilter, setDateFilter] = useState({ startDate: null, endDate: null });
  const { jsonApi } = useApis();

  useEffect(() => {
    fetchDepartments(search);
  }, [pagination.current, pagination.pageSize, search, dateFilter]); // Add dateFilter to dependencies

  const fetchDepartments = async (search = "") => {
    setLoading(true);
    const params = {
      search,
      ...(dateFilter.startDate && { startDate: dateFilter.startDate }),
      ...(dateFilter.endDate && { endDate: dateFilter.endDate })
    };
    
    const response = await jsonApi(
      appConstant.allDepartment,
      params,
      {},
      null
    );
    setLoading(false);

    if (response.data.code === 200 && response.data.status === "success") {
      setData(
        response.data.data.departments.map((department) => ({
          key: department.id,
          id: department.id,
          name: department.name,
          email: department.email,
          phone: department.phone,
          password: department.password,
          status: department.status === "active", // Ensure status is boolean
          createdAt: department.createdAt,
          updatedAt: department.updatedAt,
        }))
      );
      
      // Update pagination with the total count
      setPagination(prev => ({
        ...prev,
        total: response.data.data.totalDepartments
      }));
    }
  };

  const showModal = (mode, department = null) => {
    setModalMode(mode);
    setSelectedDepartment(department);
    setIsModalVisible(true);
    if (mode === "view" && department) {
      form.setFieldsValue({
        ...department,
        status: department.status === "active", // Ensure the status is properly set to a boolean
      });
    } else if (mode === "edit" && department) {
      form.setFieldsValue({
        name: department.name,
        email: department.email,
        phone: department.phone,
      });
    } else if (mode === "add") {
      form.resetFields();
      const newId = `DEP-${String(data.length + 1).padStart(3, "0")}`;
      form.setFieldValue("id", newId);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const status = values.status === "Active" ? "active" : "inactive";

    if (modalMode === "add") {
      const response = await jsonApi(
        appConstant.createDepartment,
        null,
        values,
        null,
        {
          name: values.name,
          email: values.email,
          phone: values.phone,
          password: values.password,
          status: status,
        }
      );

      if (response.data) {
        message.success("Department added successfully");
        fetchDepartments();
        setIsModalVisible(false);
      } else {
        message.error(response.message);
      }
    } else if (modalMode === "edit") {
      const response = await jsonApi(
        appConstant.editDepartment,
        { id: selectedDepartment.id },
        {
          name: values.name,
          email: values.email,
          phone: values.phone,
        },
        "put"
      );

      if (response.data && response.data.code === 200) {
        message.success("Department updated successfully");
        fetchDepartments();
        setIsModalVisible(false);
      } else {
        message.error(response.message || "Failed to update department");
      }
    }
    setLoading(false);
  };

  const handleStatusUpdate = async (status) => {
    setLoading(true);
    const newStatus = status === "Active" ? "active" : "inactive"; // Convert to appropriate value
    console.log(
      "selectedDepartmentForStatus.id,",
      selectedDepartmentForStatus.id
    );
    try {
      const response = await jsonApi(
        appConstant.updateDepartment,
        { id: selectedDepartmentForStatus.id }, // Passing the ID as part of the query
        { status: newStatus },
        null
      );

      if (
        response &&
        response.data &&
        response.data.code === 200 &&
        response.data.status === "success"
      ) {
        message.success("Department status updated successfully");
        fetchDepartments(); // Refresh department list
        setIsStatusModalVisible(false); // Close modal
      } else {
        message.error("Failed to update department status");
      }
    } catch (error) {
      message.error("An error occurred while updating department status");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    Modal.confirm({
      title: "Delete Confirmation",
      content: "Are you sure you want to delete this user?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        setLoading(true);
        try {
          const response = await jsonApi(
            appConstant.deleteDepartment,
            { id: userId },
            null,
            "delete"
          );

          console.log("Delete Response:", response);

          if (response?.data?.code === 200 || response?.status === 200) {
            message.success("Department data deleted successfully");
            setData((prevUsers) =>
              prevUsers.filter((user) => user.id !== userId)
            );
          } else {
            message.error(response.message || "Failed to delete user");
          }
        } catch (error) {
          console.error("Delete User Error:", error);
          message.error("An error occurred while deleting the user");
        } finally {
          setLoading(false);
        }
      },
    });
  };
  const columns = [
    {
      title: "S.No",
      key: "serialNumber",
      render: (_, __, index) => {
        const { current, pageSize } = pagination;
        return (current - 1) * pageSize + index + 1;
      },
      width: "5%",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => {
        // Check for undefined or null before calling localeCompare
        const nameA = a.name || "";
        const nameB = b.name || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => {
        const emailA = a.email || "";
        const emailB = b.email || "";
        return emailA.localeCompare(emailB);
      },
    },
  
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
      sorter: (a, b) => a.status - b.status, 
    },

    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => {
        // Ensure that createdAt values are valid dates
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateA - dateB;
      },
      render: (date) => new Date(date).toLocaleDateString(),
      width: "13%",
    },
    {
      title: "Actions",
      key: "action",
      render: (_, record) => (
        <Space
          size="middle"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Button type="link" onClick={() => showModal("view", record)}>
            View
          </Button>
          <Button type="link" onClick={() => showModal("edit", record)}>
            Edit
          </Button>
          <Button type="link" onClick={() => showStatusUpdateModal(record)}>
            Update Status
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </Space>
      ),
      width: "25%",
      align: "center",
    },
  ];

  const showStatusUpdateModal = (department) => {
    setSelectedDepartmentForStatus(department); // Set the selected department
    setIsStatusModalVisible(true); // Open the modal
    form.setFieldValue("status", department.status ? "Active" : "Inactive"); // Set initial status value
  };

  const renderStatusModalContent = () => {
    return (
      <>
        <Form
          form={form} 
          layout="vertical"
          onFinish={(values) => handleStatusUpdate(values.status)}
        >
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select the status" }]}
          >
            <Select>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Form.Item>
          <div style={{ textAlign: "center" }}>
            <Button
              onClick={() => setIsStatusModalVisible(false)}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Update Status
            </Button>
          </div>
        </Form>
      </>
    );
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleFilteredData = (data) => {
    if (data && Array.isArray(data.data)) {
      setData(data.data);

      // Update pagination with the total count
      setPagination((prev) => ({
        ...prev,
        total: data.total,
      }));
    } else {
      // If no data is received, fetch all departments
      fetchDepartments(search);
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          flexDirection: "column",
          
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0, color: isDarkMode ? "#ffffff" : undefined }}>
            Departments
          </h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal("add")}
          >
            Add Department
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DepartmentFilter
            isDarkMode={isDarkMode}
            onSearch={(dates) => {
              setDateFilter(dates);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            onDataReceived={handleFilteredData}
          />
          <Input
            placeholder="Search departments..."
            prefix={<SearchOutlined />}
            style={{ width: 250 }}
            value={search}
            onChange={(e) => {
              const value = e.target.value;
              setSearch(value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            suffix={search && (
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => {
                  setSearch('');
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
              />
            )}
          />
        </div>
      </div>
      <Card
        style={{
          background: isDarkMode ? "#1f1f1f" : "#ffffff",
          borderColor: isDarkMode ? "#303030" : "#f0f0f0",
        }}
      >
        {loading ? (
          <Spin
            size="large"
            style={{ display: "block", textAlign: "center", marginTop: "50px" }}
          />
        ) : (
          <>            
              <Table
                columns={columns}
                dataSource={data}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: data.length, 
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "30", "50"],
                  onChange: (page, pageSize) => {
                    setPagination({
                      current: page,
                      pageSize: pageSize,
                    });
                  },
                }}
                onChange={handleTableChange}
              />
          </>
        )}
      </Card>

      <Modal
        title={
          modalMode === "add"
            ? "Add Department"
            : modalMode === "edit"
            ? "Edit Department"
            : "Department Details"
        }
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={
          modalMode === "view"
            ? []
            : [
                <Button key="cancel" onClick={handleCancel}>
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  onClick={() => form.submit()}
                  loading={loading}
                >
                  {modalMode === "add" ? "Add" : "Save"}
                </Button>,
              ]
        }
      >
        {modalMode === "view" ? (
          <div>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Name">
                {selectedDepartment?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedDepartment?.email}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedDepartment?.phone}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {selectedDepartment?.status ? "Active" : "Inactive"}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(selectedDepartment?.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {new Date(selectedDepartment?.updatedAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ status: true }}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[
                {
                  required: true,
                  message: "Please input the department name",
                },
                {
                  pattern: /^[A-Za-z\s,-]+$/,
                  message:
                    "Department name can only contain alphabets, commas, and hyphens",
                },
              ]}
            >
              <Input placeholder="Enter department name" maxLength={50} />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please input the email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email" maxLength={50} />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Phone"
              rules={[
                { required: true, message: "Please input the phone number" },
                {
                  pattern: /^\d{10}$/,
                  message: "Please enter a valid 10-digit phone number",
                },
              ]}
            >
              <Input placeholder="Enter phone number" maxLength={10} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Status Update Modal */}
      <Modal
        title="Update Status"
        visible={isStatusModalVisible}
        onCancel={() => setIsStatusModalVisible(false)}
        footer={null}
      >
        {renderStatusModalContent()}
      </Modal>
    </div>
  );
};

export default Departments;
